/**
 * Declarative config of the piece upload zone (fiches 22975 / 22976) of the
 * shared `PiecesCard` — every text and the size limit, bound per usage
 * (`extranet.contractPiecesCard`, `extranet.claimPiecesCard`). A usage
 * without it (e.g. the global « Pièces à fournir » list, 22780 — unspecified)
 * offers no upload.
 *
 * Rule surface of the zone (the card's `items` / `actions` buckets):
 * - item `depot` — `hideField depot` hides the zone; its label is the zone's
 *   accessible name (« Déposer un fichier : <pièce> »), its tooltip shows on
 *   hover of the zone;
 * - action `submit` — `setActionLabel` renames « Valider »; `hideAction`
 *   hides the whole zone (a zone without its button could not upload).
 */

import type { InfoItemDescriptor } from '@components/shared/configurable';

import { PieceUploadError } from '../../api/pieceUploadService';

/**
 * Max raw file size: 5 MiB. The whole JSON request (base64 = +33 %, plus the
 * envelope) must stay under PHP `post_max_size` = 8M (contract §6 « Body
 * size »). Checked before the file is read.
 */
export const PIECE_UPLOAD_MAX_FILE_SIZE = 5 * 1024 * 1024;

/** Stable key of the upload zone in the card's `items` bucket. */
export const PIECE_DEPOT_KEY = 'depot';

/** Stable key of the « Valider » button in the card's `actions` bucket. */
export const PIECE_UPLOAD_SUBMIT_ACTION = 'submit';

/** Every text of the upload zone. `{max}` = the formatted size limit (« 5 Mo »). */
export interface PieceUploadText {
  /** Accessible name of the zone (the `depot` item label), followed by « : <pièce> ». */
  zoneLabel: string;
  /** Emphasised start of the drop-zone prompt (« Cliquez »). */
  click: string;
  /** Rest of the prompt (« ou glissez un fichier ici »). */
  drop: string;
  /** Shown next to the chosen file name (green). */
  attached: string;
  /** Submit button. */
  submit: string;
  /** Accessible name of the × that resets the zone. */
  reset: string;
  /** File over {@link PieceUploadConfig.maxFileSize} (`{max}` token). */
  fileTooLarge: string;
  /** The browser could not read the file. */
  readError: string;
  /** Call 1 failed / refused without a VEOS message. */
  documentError: string;
  /** Call 2 failed after the document was archived (no rollback). */
  pieceUpdateError: string;
  /** Success toast title. */
  successTitle: string;
  /** Success toast message. */
  success: string;
}

export interface PieceUploadConfig {
  /** Max raw file size in bytes. */
  maxFileSize: number;
  text: PieceUploadText;
}

/** Default config of the contract and claim « Pièces » tabs. */
export const PIECE_UPLOAD_CONFIG: PieceUploadConfig = {
  maxFileSize: PIECE_UPLOAD_MAX_FILE_SIZE,
  text: {
    zoneLabel: 'Déposer un fichier',
    click: 'Cliquez',
    drop: 'ou glissez un fichier ici',
    attached: 'Fichier joint',
    submit: 'Valider',
    reset: 'Retirer le fichier',
    fileTooLarge: 'Le fichier dépasse la taille maximale autorisée ({max}).',
    readError: 'Le fichier n’a pas pu être lu. Veuillez réessayer.',
    documentError: 'Le dépôt du document a échoué. Veuillez réessayer.',
    pieceUpdateError:
      'Le document a bien été transmis, mais la pièce n’a pas pu être mise à jour. ' +
      'Contactez votre interlocuteur avant de le déposer à nouveau.',
    successTitle: 'Pièce transmise',
    success: 'Votre document a bien été transmis.',
  },
};

/** The `depot` item descriptor of a config (published with the piece items). */
export function pieceDepotField(config: PieceUploadConfig): InfoItemDescriptor {
  return { key: PIECE_DEPOT_KEY, label: config.text.zoneLabel, order: 3 };
}

/** A byte count in French units: `5 Mo`, `512 Ko`, `1,5 Mo`. */
export function formatFileSizeFr(bytes: number): string {
  const mo = bytes / (1024 * 1024);
  if (mo >= 1) return `${mo.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} Mo`;
  const ko = Math.max(1, Math.round(bytes / 1024));
  return `${ko.toLocaleString('fr-FR')} Ko`;
}

/** The « file too large » message of a config, `{max}` resolved. */
export function fileTooLargeMessage(config: PieceUploadConfig): string {
  return config.text.fileTooLarge.replaceAll('{max}', formatFileSizeFr(config.maxFileSize));
}

/** A failed upload as shown to the user (error `Alert`). */
export interface PieceUploadFailure {
  title?: string;
  message: string;
}

/**
 * The user-facing message of a failed upload: call 1 → the VEOS / proxy
 * message, else `documentError`; call 2 → `pieceUpdateError` (+ the VEOS
 * message below it); unreadable file → `readError`.
 */
export function uploadFailureOf(error: unknown, text: PieceUploadText): PieceUploadFailure {
  if (!(error instanceof PieceUploadError)) return { message: text.documentError };
  switch (error.stage) {
    case 'read':
      return { message: text.readError };
    case 'piece':
      return error.detail === null
        ? { message: text.pieceUpdateError }
        : { title: text.pieceUpdateError, message: error.detail };
    default:
      return { message: error.detail ?? text.documentError };
  }
}
